import { Button } from '@/components/ui/button'

/**
 * What the `free-watermarked-preview` experiment shows, on every video surface
 * that runs it: one card before the prompt is written, one banner after the
 * result lands. Shared so the two Seedance pages and the spec-driven /tools
 * studios make the same promise in the same words — the copy IS the treatment
 * being measured, and a page paraphrasing it is a page in its own experiment.
 */

/** Stands in for the credit badge: this org has one free run, so a balance and
 *  a per-clip price are both true and both beside the point. */
export function FreePreviewCreditCard({
  onUpgrade,
}: {
  onUpgrade: () => void
}) {
  return (
    <div className="border-border bg-muted/50 rounded-xl border p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-muted-foreground text-[10px] tracking-[0.3em] uppercase">
            Free preview
          </p>
          <p className="text-foreground mt-1 text-sm font-semibold">
            One free generation{' '}
            <span className="text-muted-foreground font-normal">
              · watermarked
            </span>
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full"
          onClick={onUpgrade}
        >
          See plans
        </Button>
      </div>
      <p className="text-muted-foreground mt-2 text-xs">
        Your first run is on us, with a watermark. A plan from $19/mo unlocks
        the clean file of this same result.
      </p>
    </div>
  )
}

/** Sits under the finished result. The upgrade is the experiment's primary
 *  metric, so the click is tracked by the caller that knows the surface. */
export function FreePreviewResultBanner({
  onUpgrade,
  testId = 'free-preview-banner',
}: {
  onUpgrade: () => void
  testId?: string
}) {
  return (
    <div
      className="border-border bg-muted/50 flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4"
      data-testid={testId}
    >
      <div>
        <p className="text-foreground text-sm font-semibold">
          This is a watermarked preview
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          The clean version of this exact result is ready. Upgrade to download
          it without the watermark.
        </p>
      </div>
      <Button size="sm" className="rounded-full" onClick={onUpgrade}>
        Remove watermark
      </Button>
    </div>
  )
}
